/* eslint-disable */
import React, { useContext, useMemo, useState } from "react";
import { capitalize } from "utils/formatters";
import { alphabeticalOnlyRegex } from "constants/regex";
import { FormControl, OutlinedInput } from "@mui/material";
import { Grid, Paper, Button, CircularProgress } from "@material-ui/core";
import Typography from "@mui/material/Typography";
import { useValidEmail } from "../../hooks/useValidEmail";
import {
  btnStyle,
  h3Style,
  inputStyle,
  logoStyle,
  paperStyle,
  pStyle,
} from "./styles";
import { AuthContext } from "../../contexts/authContext";
import BatchAdminInvite from "./BatchAdminInvite/BatchAdminInvite";
import { createPatientOnInvitePromise } from "repositories/patientApi";
import { useAppSelector } from "hooks/reduxHooks";
import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import { USER_GROUPS } from "constants/roles";
import { InviteResultModal } from "./InviteResultModal";
import { ClinicRepository } from "repositories/ClinicRepository";
import EmailListModal from "./EmailListModal";
import { createClinicianPromise } from "repositories/coachApi";

const InviteUser: React.FC<any> = () => {
  const { email: clinicianEmail, setEmail: setClinicianEmail } =
    useValidEmail("");
  const {
    email: coachEmail,
    setEmail: setCoachEmail,
    emailIsValid: coachEmailIsValid,
  } = useValidEmail("");
  const {
    email: patientEmail,
    setEmail: setPatientEmail,
    emailIsValid: patientEmailIsValid,
  } = useValidEmail("");

  const authContext = useContext(AuthContext);
  const [genericErrorMessage, setGenericErrorMessage] = useState("");
  const [coachName, setCoachName] = useState({ firstName: "", lastName: "" });
  const [clinicianName, setClinicianName] = useState({
    firstName: "",
    lastName: "",
  });
  const [open, setOpen] = React.useState(false);
  const [isError, setError] = React.useState(false);
  const [mailModalOpen, setMailModalOpen] = React.useState(false);

  const [errorMessage, setErrorMessage] = React.useState("");

  const [isLoading, setIsLoading] = React.useState(false);

  const currentClinician = useAppSelector(selectCurrentClinician);

  const theme = useMemo(() => {
    return authContext.globalConfig?.theme as unknown as undefined;
  }, [authContext]);

  const onInviteClinician = async (e: any, userType: string) => {
    e.stopPropagation();
    setIsLoading(true);

    try {
      if (clinicianEmail) {
        await inviteStaff(
          clinicianEmail,
          userType,
          clinicianName.firstName,
          clinicianName.lastName
        );
        setClinicianEmail("");
        setClinicianName({ firstName: "", lastName: "" });
      } else if (coachEmail) {
        await inviteStaff(
          coachEmail.toLowerCase(),
          userType,
          coachName.firstName,
          coachName.lastName
        );
        setCoachEmail("");
        setCoachName({ firstName: "", lastName: "" });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };
  async function inviteStaff(
    email: string,
    userType: string,
    firstName: string,
    lastName: string
  ) {
    setErrorMessage("");
    try {
      // FIRST STEP: adding new Clinician to the internal DB with role
      const response = await createClinicianPromise({
        email,
        firstName,
        lastName,
        clinicId: currentClinician?._id ?? "",
        role: [userType],
      });

      // SECOND STEP: Create user in cognito

      await authContext.signUpAdmin(email);
      // THIRD STEP: assigment specific role
      if (authContext.adminAddUserToGroup) {
        if (userType == USER_GROUPS.CLINICIAN) {
          await authContext.adminAddUserToGroup(email, USER_GROUPS.CLINICIAN);
        } else if (userType == USER_GROUPS.COACH) {
          await authContext.adminAddUserToGroup(email, USER_GROUPS.COACH);
        }
      }
      setError(false);
      setOpen(true);
    } catch (error: any) {
      setErrorMessage(
        typeof error.response?.data === "string"
          ? error.response?.data
          : error.response?.data?.message
      );
      setError(true);
      setOpen(true);
    }
  }

  const onInvitePatient = async (
    e: any,
    email?: string,
    avoidOpenModal?: boolean
  ) => {
    e.stopPropagation();
    const patientEmailParsed = email || patientEmail;
    if (!patientEmailParsed || !currentClinician?._id) return;

    try {
      setIsLoading(true);
      let userType = [];
      // FIRST STEP: adding new Athlete or Patient to the internal DB with role
      if (currentClinician?.role?.includes(USER_GROUPS.COACH)) {
        userType.push(USER_GROUPS.ATHLETE);
      } else if (currentClinician?.role?.includes(USER_GROUPS.CLINICIAN)) {
        userType.push(USER_GROUPS.PATIENT);
      }

      await createPatientOnInvitePromise({
        email: patientEmailParsed.toLowerCase(),
        clinicianId: currentClinician?._id,
        clinicId: currentClinician?.clinicId ?? "",
        role: userType,
      });
      // SECOND STEP: Create user in cognito
      await authContext.signUpAdmin(patientEmailParsed.toLowerCase());
      // THIRD STEP: assigment specific role (ATHLETE / Patient)
      if (authContext.adminAddUserToGroup) {
        if (currentClinician?.role?.includes(USER_GROUPS.COACH)) {
          await authContext.adminAddUserToGroup(
            patientEmailParsed.toLowerCase(),
            USER_GROUPS.ATHLETE
          );
        } else if (currentClinician?.role?.includes(USER_GROUPS.CLINICIAN)) {
          await authContext.adminAddUserToGroup(
            patientEmailParsed.toLowerCase(),
            USER_GROUPS.PATIENT
          );
        }
      }
      setIsLoading(false);
      if (avoidOpenModal) {
        return { hasError: false };
      }

      setError(false);

      setOpen(true);
    } catch (error: any) {
      setIsLoading(false);
      const errMessage =
        error.response.data.message || error.message || "Unexpected error";
      if (avoidOpenModal) {
        return { hasError: true, errorMessage: errMessage };
      }
      setErrorMessage(
        typeof error.response?.data === "string"
          ? error.response?.data
          : error.response?.data?.message
      );
      setError(true);
      setOpen(true);
    }
  };

  return (
    <div
      style={{
        margin: "15px 100px",
        alignItems: "start",
        justifyContent: "start",
      }}
    >
      <Grid>
        {currentClinician?.role?.includes(USER_GROUPS.SUPER_ADMIN) && (
          <>
            <Typography variant="h5">Athletic</Typography>
            <Paper
              elevation={0}
              style={{
                ...paperStyle,
                padding: "50px",
                paddingRight: "550px",
                marginTop: "40px",
              }}
            >
              <Grid style={logoStyle}>
                <h3 style={h3Style}>Invite Coach</h3>
              </Grid>
              <Grid>
                <FormControl variant="outlined">
                  <OutlinedInput
                    error={genericErrorMessage != "" || coachEmailIsValid != ""}
                    size="small"
                    style={{ ...inputStyle, marginTop: "0" }}
                    type="text"
                    value={coachEmail}
                    onChange={(e: any) =>
                      setCoachEmail(e.target.value.replace(/ /g, ""))
                    }
                    placeholder="Email"
                  />
                </FormControl>
              </Grid>
              <Grid>
                <FormControl variant="outlined">
                  <OutlinedInput
                    error={genericErrorMessage != ""}
                    size="small"
                    style={inputStyle}
                    type="text"
                    value={coachName.firstName}
                    onChange={(e: any) => {
                      if (alphabeticalOnlyRegex.test(e.target.value)) {
                        setCoachName({
                          ...coachName,
                          firstName: capitalize(e.target.value),
                        });
                      }
                    }}
                    placeholder="First Name"
                  />
                </FormControl>
              </Grid>
              <Grid>
                <FormControl variant="outlined">
                  <OutlinedInput
                    error={genericErrorMessage != ""}
                    size="small"
                    style={inputStyle}
                    type="text"
                    label="Coach Last name"
                    value={coachName.lastName}
                    onChange={(e: any) => {
                      if (alphabeticalOnlyRegex.test(e.target.value)) {
                        setCoachName({
                          ...coachName,
                          lastName: capitalize(e.target.value),
                        });
                      }
                    }}
                    placeholder="Last Name"
                  />
                </FormControl>
              </Grid>

              <Grid>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={
                    genericErrorMessage != "" ||
                    coachEmailIsValid != "" ||
                    !coachName.firstName ||
                    !coachName.lastName ||
                    isLoading
                  }
                  style={btnStyle}
                  onClick={(e) => {
                    onInviteClinician(e, USER_GROUPS.COACH);
                  }}
                >
                  {isLoading ? <CircularProgress size={20} /> : "Send Invite"}
                </Button>
              </Grid>
            </Paper>
          </>
        )}
        {(currentClinician?.role?.includes(USER_GROUPS.CLINICIAN) ||
          currentClinician?.role?.includes(USER_GROUPS.COACH)) && (
          <Paper
            elevation={0}
            style={{
              ...paperStyle,
              padding: "50px",
              paddingRight: "550px",
              marginTop: "64px",
            }}
          >
            <Grid style={logoStyle}>
              <h3 style={h3Style}>
                Invite{" "}
                {currentClinician?.role?.includes(USER_GROUPS.COACH)
                  ? "athlete"
                  : "patient"}
              </h3>
              <p style={pStyle}>
                Please add the email address of the{" "}
                {currentClinician?.role?.includes(USER_GROUPS.COACH)
                  ? "athlete"
                  : "patient"}{" "}
                you'd like to invite
              </p>
            </Grid>
            <Grid>
              <FormControl variant="outlined">
                <OutlinedInput
                  error={genericErrorMessage != "" || patientEmailIsValid != ""}
                  size="small"
                  style={{ ...inputStyle, marginTop: "0" }}
                  type="text"
                  value={patientEmail}
                  placeholder="Email"
                  onChange={(e: any) => setPatientEmail(e.target.value)}
                />
              </FormControl>
            </Grid>

            <Grid>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                style={btnStyle}
                disabled={
                  genericErrorMessage != "" ||
                  patientEmailIsValid != "" ||
                  isLoading
                }
                onClick={(e) => {
                  onInvitePatient(e);
                }}
              >
                {isLoading ? <CircularProgress size={20} /> : "Send Invite"}
              </Button>
            </Grid>
            <p style={{ color: "red", marginTop: "40px" }}>{errorMessage}</p>

            <Grid>
              <p style={{ ...pStyle, color: "#000000" }}>or</p>
              <p style={pStyle}>
                You can copy/paste a the emails separated by comma.
              </p>
            </Grid>
            <div
              aria-hidden="true"
              className="upload-users-container"
              style={{
                width: "120px",
                justifyContent: "left",
                color: "white",
                ...btnStyle,
              }}
              onClick={() => {
                setMailModalOpen(true);
              }}
            >
              <span className="upload-users-label">
                <p>Paste emails</p>
              </span>
            </div>
          </Paper>
        )}
      </Grid>
      <EmailListModal
        inviteFunction={onInvitePatient}
        setIsOpen={setMailModalOpen}
        isOpen={mailModalOpen}
      />
      <InviteResultModal
        open={open}
        setOpen={setOpen}
        isError={isError}
        extraWidth={Boolean(errorMessage)}
        failMessage={errorMessage}
      />
    </div>
  );
};

export default InviteUser;
